import * as ko from 'knockout';

import { ListRequestParams } from '../api/request';
import { ListLoaderDelegate, ListLoader, delegateActions } from '../components/list_loader';
import { NameI18n } from '../models/name';
import * as namesApi from '../api/names';
import { session } from '../session';
import { ListHeaderAction } from '../components/list_header';
import i18n from '../i18n';
import { translate } from '../i18n_text';
import { reactListTablesEnabled } from '../permissions';
import { GridView, pageParams } from '../utils/grid_view';
import { NamesListPage, NamesListProps, NamesListQuery, NamesListRow } from 'react-islands';

let template = require('raw-loader!../../templates/names.html').default;

interface DefaultFilters {
  name_search?: string;
}

export class NamesScreen implements ListLoaderDelegate<namesApi.NameI18nData, NameI18n> {
  private baseName: string;
  private hrefBaseName: string;
  title: string;
  canCreate: boolean;
  listActions = ko.observableArray<ListHeaderAction>([]);
  nameFilter = ko.observable('').extend({ throttle: 300 });
  searchPlaceholder = ko.pureComputed(() => i18n.t(`Search ${this.title.toLowerCase()}`)());

  newNamesTable = reactListTablesEnabled();

  private view = new GridView('names');

  tableProps = ko.pureComputed<NamesListProps>(() => ({
    view: this.view,
    fetchPage: this.fetchPage,
    exportFileName: this.baseName.replace(/_/g, '-'),
  }));

  private subscriptions: KnockoutSubscription[] = [];

  constructor(params: {
    baseName: string;
    title: string;
    hrefBaseName?: string;
    filters: DefaultFilters;
    canEdit?: () => boolean;
  }) {
    this.baseName = params.baseName;
    this.hrefBaseName = params.hrefBaseName || params.baseName;
    this.title = params.title;
    this.canCreate = params.canEdit ? params.canEdit() : session.isAtLeastEditor();

    this.nameFilter(params.filters.name_search || '');

    if (this.canCreate) {
      this.listActions.push({
        title: i18n.t('Add')(),
        icon: 'add_circle',
        href: '/' + this.hrefBaseName + '/new/',
      });
    }

    if (this.newNamesTable) {
      this.view.load(params.filters);
    }
  }

  private fetchPage = (query: NamesListQuery): Promise<NamesListPage> =>
    this.view.fetchPage(
      query,
      (options) => namesApi.list(this.baseName, pageParams(query, 'name_prefix'), undefined, options),
      this
    );

  toRow = (name: NameI18n): NamesListRow => ({
    id: name.id(),
    name: translate(name.nameJson()),
    url: session.toTenantPath(name.editUrl),
    actions: delegateActions(this, name, this.view.reload),
  });

  fetch(params: ListRequestParams) {
    return namesApi.list(this.baseName, {
      ...params,
      name_search: this.nameFilter(),
    });
  }

  instantiate(nameData: namesApi.NameI18nData) {
    return new NameI18n(this.hrefBaseName, nameData, this.canCreate);
  }

  getEditUrl(entity: NameI18n) {
    return entity.editUrl;
  }

  remove(id: string) {
    return namesApi.remove(this.baseName, id);
  }

  canRemove(name: NameI18n) {
    return name.canEdit;
  }

  onReady(loader: ListLoader<namesApi.NameI18nData, NameI18n>) {
    this.subscriptions.push(this.nameFilter.subscribe(() => loader.forceLoad()));
  }

  dispose() {
    this.subscriptions.forEach((s) => s.dispose());
    this.subscriptions = [];
  }
}

export let names = {
  name: 'names',
  viewModel: NamesScreen,
  template: template,
};

ko.components.register(names.name, names);
